import { z } from "zod";
/**
 * Delivery health of one channel of a person: the client wrote there, a
 * delivery was confirmed, the provider rejected it (with its code; a later
 * client message clears it), a send was tried, or nothing was tried yet.
 */
export const ContactChannelHealthSchema = z.object({
    state: z.enum(["client_wrote", "delivered", "rejected", "tried", "never_tried"]),
    customerAt: z.string().datetime().nullable(),
    deliveredAt: z.string().datetime().nullable(),
    rejectedAt: z.string().datetime().nullable(),
    rejectedCode: z.string().regex(/^[A-Z0-9_]{1,100}$/u).nullable(),
    lastTriedAt: z.string().datetime().nullable(),
}).strict();
/**
 * One channel of the person behind a deal or a booking: an existing chat on a
 * personal route (possibly linked to another deal of the same CRM contact) or
 * an address where a new chat can be opened. Addresses are always masked.
 */
export const ContactChannelSchema = z.object({
    channelKey: z.string().min(5).max(300),
    kind: z.enum(["chat", "open"]),
    conversationId: z.string().uuid().nullable(),
    channelType: z.enum(["whatsapp", "telegram"]),
    channelLabel: z.string().trim().min(1).max(100),
    addressKind: z.enum(["phone", "telegram_id", "telegram_username"]),
    addressMasked: z.string().trim().min(1).max(80),
    dealExternalId: z.string().nullable(),
    otherDeal: z.boolean(),
    health: ContactChannelHealthSchema,
    available: z.boolean(),
    unavailableReason: z.string().max(60).nullable(),
    ruleReason: z.string().max(60).nullable(),
    calendarCapable: z.boolean().optional(),
    // Staff deal screen only: the client's latest message in a chat kept under
    // another deal, so a reply there is readable from the current deal.
    lastCustomerMessage: z.object({
        text: z.string().max(300),
        occurredAt: z.string().datetime(),
    }).strict().nullable().optional(),
    chosen: z.boolean().optional(),
    chosenBy: z.enum(["staff", "rule"]).nullable().optional(),
    staffChoice: z.boolean().optional(),
    // A rejected Telegram chat without provider history: Wazzup cannot write
    // first by a Telegram id, so the refused send went to the CRM phone or
    // username. This is that address (masked), shown instead of the chat id.
    rejectedVia: z.object({
        addressKind: z.enum(["phone", "telegram_username"]),
        addressMasked: z.string().trim().min(1).max(80),
    }).strict().nullable().optional(),
}).strict();
/** Refusals of the address a new Telegram chat is opened by (vekha.telegram_first_contact_rejection). */
export function telegramFirstContactRejectionKind(code) {
    if (code === "CHANNEL_TGAPI_PHONE_NOT_OCCUPIED")
        return "phone";
    if (code === "CHANNEL_TGAPI_USERNAME_NOT_OCCUPIED" || code === "CHANNEL_TGAPI_USERNAME_INVALID")
        return "telegram_username";
    return null;
}
/**
 * Marks each rejected Telegram chat whose refused send actually went to the
 * CRM phone or username with that address: the open Telegram row of the same
 * line and address kind carries it (it is rejected by the same refusal).
 */
export function withTelegramFirstContactAttempts(channels) {
    return channels.map((channel) => {
        const kind = channel.kind === "chat" && channel.channelType === "telegram" && channel.health.state === "rejected"
            ? telegramFirstContactRejectionKind(channel.health.rejectedCode) : null;
        if (!kind)
            return channel;
        const twin = channels.find((other) => other.kind === "open" && other.channelType === "telegram"
            && other.channelLabel === channel.channelLabel && other.addressKind === kind);
        return twin ? { ...channel, rejectedVia: { addressKind: kind, addressMasked: twin.addressMasked } } : channel;
    });
}
export const ContactChannelListSchema = z.object({
    channels: z.array(ContactChannelSchema).max(40),
}).strict();
//# sourceMappingURL=contact-channels.js.map